// Yoga Nunney - Village Location Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaNunney = () => {
  usePageMeta({
    title: "Yoga Classes Nunney | Annelie Lily",
    description: "Yoga classes for Nunney, Somerset. Small group yoga in Frome, just 5 minutes from Nunney village. Hatha, Yin & private sessions.",
    canonical: "https://annelielily.com/areas/yoga-nunney"
  });

  const locationSchema = generateLocationSchema({
    townName: "Nunney",
    description: "Yoga classes for Nunney village at Annelie Lily Yoga in Frome. Just 5 minutes away.",
    url: "https://annelielily.com/yoga-nunney"
  });

  const faqs = [
    { question: "How close is Frome to Nunney?", answer: "About 5 minutes by car. Nunney is one of the closest villages — just head into Frome on the A361." },
    { question: "What times are classes available?", answer: "I run classes at various times through the week, including evenings. Get in touch for the current schedule." },
    { question: "Do I need my own yoga mat?", answer: "I have mats available, but you're welcome to bring your own if you prefer." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Nunney" currentSlug="areas/yoga-nunney" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Nunney</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Nunney</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Nunney's castle village charm is just 5 minutes from Frome. If you're looking for 
              yoga that's close to home, personal, and welcoming — you've found it.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />5 min from Nunney</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Nunney Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Nunney is a gorgeous village with that famous castle, but it's a small place — 
              not exactly overflowing with yoga options. The good news is Frome is just down the road, 
              and my studio offers the kind of small, personal classes you won't find at a gym.
            </p>
            <p>
              I teach <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> for 
              those wanting to build strength and flexibility, and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link> for 
              those needing slower, deeper release. Classes are kept to 6-8 people so I can actually 
              see what's happening in your practice.
            </p>
            <p>
              <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">Never done yoga before?</Link> That's 
              fine — many of my students started knowing nothing. I teach in a way that welcomes all levels.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Nunney</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga", desc: "Active practice combining postures, breathing, and relaxation.", link: "/yoga-classes/hatha-yoga" },
              { title: "Yin Yoga", desc: "Slow, meditative stretches for deep release.", link: "/yoga-classes/yin-yoga" },
              { title: "Private Sessions", desc: "One-to-one yoga designed around you.", link: "/yoga-classes/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions from Nunney Residents</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Start?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Nearby areas:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Mells", link: "/areas/yoga-mells" },
              { name: "Beckington", link: "/areas/yoga-beckington" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Somerset", link: "/areas/yoga-somerset" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaNunney;
